import React from 'react';
import './Sidebar.css';

function HistoryItem({ entry, onClick, active }) {
  const findingCount = (entry.result?.findings || []).length;

  return (
    <div className={`history-item ${active ? 'active' : ''}`} onClick={onClick}>
      <div className="history-info">
        <div className="history-name">{entry.filename}</div>
        <div className="history-meta">
          <span>{findingCount} finding{findingCount !== 1 ? 's' : ''}</span>
        </div>
      </div>
    </div>
  );
}

function Sidebar({
  activeModule,
  onModuleChange,
  apiStatus,
  history,
  activeHistory,
  onHistorySelect,
  templateStats,
  mobileMenuOpen,
  onMobileMenuClose,
  user,
  onLogout,
}) {
  const totalTemplates = templateStats?.total_templates ?? 0;
  const hasTemplates = totalTemplates > 0;

  return (
    <aside className={`sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`}>
      {/* ─── Mobile Close Button ─── */}
      {mobileMenuOpen && (
        <button 
          className="sidebar-mobile-close"
          onClick={onMobileMenuClose}
          aria-label="Close menu"
        >
          ✕
        </button>
      )}
      {/* ─── Logo ─── */}
      <div className="sidebar-logo">
        <div className="sidebar-logo-icon">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path d="M12 2L3 7v10l9 5 9-5V7L12 2z" stroke="#7c5cfc" strokeWidth="1.5" fill="rgba(124,92,252,0.12)" strokeLinejoin="round"/>
            <path d="M12 8l-4 2.5v5L12 18l4-2.5v-5L12 8z" fill="#7c5cfc" opacity="0.7"/>
          </svg>
        </div>
        <div>
          <div className="sidebar-logo-title">FraudLens</div>
          <div className="sidebar-logo-sub">Invoice Intelligence</div>
        </div>
      </div>

      {/* ─── API Status ─── */}
      <div className="sidebar-status">
        <span className={`sidebar-status-dot ${apiStatus}`} />
        <span className="sidebar-status-text">API {apiStatus === 'online' ? 'Online' : apiStatus === 'offline' ? 'Offline' : 'Checking…'}</span>
        {apiStatus === 'online' && <span className="sidebar-status-ping" />}
      </div>

      {/* ─── Navigation ─── */}
      <nav className="sidebar-nav">
        <div className="sidebar-nav-label">Modules</div>

        <button
          className={`sidebar-nav-btn ${activeModule === 'invoice-analysis' ? 'active' : ''}`}
          onClick={() => onModuleChange('invoice-analysis')}
        >
          <span className="sidebar-nav-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="16" y1="13" x2="8" y2="13"/>
              <line x1="16" y1="17" x2="8" y2="17"/>
              <polyline points="10 9 9 9 8 9"/>
            </svg>
          </span>
          <span className="sidebar-nav-text">Invoice Analysis</span>
        </button>

        <button
          className={`sidebar-nav-btn ${activeModule === 'template-intelligence' ? 'active' : ''}`}
          onClick={() => onModuleChange('template-intelligence')}
        >
          <span className="sidebar-nav-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
            </svg>
          </span>
          <span className="sidebar-nav-text">Template Intelligence</span>
          {hasTemplates && (
            <span className="sidebar-nav-badge">{totalTemplates}</span>
          )}
        </button>

        <button
          className={`sidebar-nav-btn ${activeModule === 'bulk-analysis' ? 'active' : ''}`}
          onClick={() => onModuleChange('bulk-analysis')}
        >
          <span className="sidebar-nav-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="7" height="7"/>
              <rect x="14" y="3" width="7" height="7"/>
              <rect x="3" y="14" width="7" height="7"/>
              <rect x="14" y="14" width="7" height="7"/>
            </svg>
          </span>
          <span className="sidebar-nav-text">Bulk Analysis</span>
        </button>

        {/* User Management — admin only */}
        {user?.is_admin && (
          <button
            className={`sidebar-nav-btn ${activeModule === 'user-management' ? 'active' : ''}`}
            onClick={() => onModuleChange('user-management')}
          >
            <span className="sidebar-nav-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
            </span>
            <span className="sidebar-nav-text">User Management</span>
          </button>
        )}
      </nav>

      {/* ─── History ─── */}
      {activeModule === 'invoice-analysis' && history.length > 0 && (
        <div className="sidebar-history">
          <div className="sidebar-nav-label">Recent Analyses
            <span className="sidebar-history-count">{history.length}</span>
          </div>
          <div className="sidebar-history-list">
            {history.map((h, i) => (
              <HistoryItem
                key={i}
                entry={h}
                active={activeHistory === i}
                onClick={() => onHistorySelect(i)}
              />
            ))}
          </div>
        </div>
      )}

      {/* ─── User Info + Logout ─── */}
      {user && (
        <div className="sidebar-user">
          <div className="sidebar-user-info">
            <div className="sidebar-user-email" title={user.email}>{user.email}</div>
            {user.is_admin && <span className="sidebar-user-badge">Admin</span>}
          </div>
          <button className="sidebar-logout-btn" onClick={onLogout} title="Sign out">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
              <polyline points="16 17 21 12 16 7"/>
              <line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
            Sign Out
          </button>
        </div>
      )}

      {/* ─── Footer ─── */}
      <div className="sidebar-footer">
        <div className="sidebar-footer-stack">
          <span>GPT-4o</span>
          <span className="sidebar-footer-dot">·</span>
          <span>FastAPI</span>
          <span className="sidebar-footer-dot">·</span>
          <span>Vision AI</span>
        </div>
      </div>
    </aside>
  );
}

export default Sidebar;
